/**
 * Mise à jour d'une application existante : nouvelle version de son code (§3.5).
 *
 * Il réutilise le composant de saisie du formulaire initial : même zone de
 * code, même dépôt de fichier, mêmes compteurs. Le nom et le domaine sont
 * seulement **affichés** — aucun champ modifiable, parce qu'une mise à jour ne
 * déplace pas une application : l'adresse publique reste la même.
 *
 * Deux différences avec le déploiement initial :
 * - le **commentaire de version est obligatoire** (§3.5). Il est validé
 *   localement par `versionCommentSchema`, le schéma partagé avec l'API : le
 *   message affiché avant l'envoi est ainsi mot pour mot celui que renverrait
 *   le serveur ;
 * - la case de confirmation est reprise à l'identique : `POST
 *   /api/apps/:id/redeploy` exige `confirmed: true`, exactement comme la
 *   création.
 */

import {
  MAX_ARTIFACT_CODE_BYTES,
  MAX_COMMENT_LENGTH,
  MAX_VERSION_COMMENT_LENGTH,
  versionCommentSchema,
  type AppVersionDto,
} from '@portail/shared';
import clsx from 'clsx';
import { Lock, Rocket } from 'lucide-react';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, redeployApp } from '../api';
import { messageOf } from '../hooks/useAsync';
import { useSession } from '../hooks/useSession';
import { formatNumber } from '../lib/format';
import { byteLength, CodeInput } from './CodeInput';
import { ConfirmationCheckbox } from './ConfirmationCheckbox';
import { DraftComparison } from './DraftComparison';
import { Button, ErrorBanner, Label } from './ui';

/** Message d'erreur du commentaire de version, ou `null` s'il est valide. */
function versionCommentError(value: string): string | null {
  const result = versionCommentSchema.safeParse(value);
  return result.success ? null : (result.error.issues[0]?.message ?? 'Commentaire invalide.');
}

/** Propriétés du formulaire de mise à jour. */
export interface RedeployFormProps {
  appId: string;
  slug: string;
  /** Domaine de l'application : affiché, jamais modifiable. */
  domain: string;
  /** Adresse publique, rappelée pour dire qu'elle ne changera pas. */
  url: string;
  /** Numéro de la version en ligne, pour annoncer celui de la suivante. */
  currentVersionNumber?: number | null;
  /** Commentaire actuel de l'application, proposé comme valeur de départ. */
  currentComment: string | null;
  /** Versions existantes, pour comparer le code saisi à l'une d'elles. */
  versions?: AppVersionDto[];
  /** Version en ligne, proposée par défaut à la comparaison. */
  currentVersionId?: string | null;
  onCancel?: () => void;
}

/** Nouveau code pour une application déjà déployée. */
export function RedeployForm({
  appId,
  slug,
  domain,
  url,
  currentVersionNumber,
  currentComment,
  versions = [],
  currentVersionId = null,
  onCancel,
}: RedeployFormProps) {
  const navigate = useNavigate();
  const { settings } = useSession();
  const commentId = useId();
  const versionCommentId = useId();

  const [code, setCode] = useState('');
  const [versionComment, setVersionComment] = useState('');
  const [versionCommentTouched, setVersionCommentTouched] = useState(false);
  const [comment, setComment] = useState(currentComment ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const versionError = versionCommentError(versionComment);

  const missing: string[] = [];
  if (code.trim().length === 0) missing.push('le nouveau code');
  if (byteLength(code) > MAX_ARTIFACT_CODE_BYTES) missing.push('un code sous la limite de 1 Mo');
  if (versionError !== null) missing.push('le commentaire de version');
  if (!confirmed) missing.push('la confirmation');

  const canSubmit = missing.length === 0 && !submitting;

  const onSubmit = (): void => {
    setVersionCommentTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setFailure(null);

    redeployApp(appId, {
      code,
      versionComment: versionComment.trim(),
      comment: comment.trim() ? comment.trim() : null,
      confirmed: true,
    })
      .then((response) => navigate(`/deploiements/${response.jobId}`))
      .catch((error: unknown) => {
        setFailure(error instanceof ApiError ? error : new ApiError(0, messageOf(error)));
        setSubmitting(false);
      });
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {/* Nom et domaine : verrouillés, et dits comme tels. */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Nom de l'application
            </dt>
            <dd className="font-mono text-slate-900 dark:text-slate-100">{slug}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Domaine
            </dt>
            <dd className="font-mono text-slate-900 dark:text-slate-100">{domain}</dd>
          </div>
        </dl>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-400">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Le nom et le domaine ne sont pas modifiables : l'adresse{' '}
          <span className="break-all font-mono">{url}</span> restera exactement la même après la
          mise à jour. L'application est indisponible quelques secondes le temps de la bascule.
          {typeof currentVersionNumber === 'number'
            ? ` La version en ligne v${currentVersionNumber} sera remplacée par la v${currentVersionNumber + 1}, et restera restaurable.`
            : null}
        </p>
      </div>

      <CodeInput
        value={code}
        onChange={setCode}
        rows={14}
        label="Nouveau code de l'artifact"
        // Les champs suivent la zone de code : sans ce recentrage, un artifact de
        // cinq cents lignes repousse le bouton de mise à jour hors de portée.
        revealFieldsOnPaste
      />

      {/* Placé juste après la saisie : c'est là qu'un auteur se demande si son
          code est bien celui qu'il croit, avant d'écraser une application en ligne. */}
      <DraftComparison
        appId={appId}
        draft={code}
        versions={versions}
        defaultVersionId={currentVersionId}
      />

      <div>
        <Label
          htmlFor={versionCommentId}
          required
          hint={`${formatNumber(versionComment.length)} / ${formatNumber(MAX_VERSION_COMMENT_LENGTH)}`}
        >
          Commentaire de version
        </Label>
        <textarea
          id={versionCommentId}
          value={versionComment}
          rows={2}
          maxLength={MAX_VERSION_COMMENT_LENGTH}
          aria-invalid={versionCommentTouched && versionError !== null}
          placeholder="Ce que change cette version : correction du formulaire, nouveau graphique…"
          onChange={(event) => setVersionComment(event.target.value)}
          onBlur={() => setVersionCommentTouched(true)}
          className={clsx(
            'w-full rounded-md border bg-white px-3 py-2 text-sm',
            'text-slate-900 dark:bg-slate-950 dark:text-slate-100',
            versionCommentTouched && versionError !== null
              ? 'border-red-400 dark:border-red-800'
              : 'border-slate-300 dark:border-slate-700',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
          )}
        />
        {versionCommentTouched && versionError !== null ? (
          <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {versionError}
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Obligatoire : c'est ce texte qui identifiera cette version dans l'historique, dans six
            mois comme aujourd'hui.
          </p>
        )}
      </div>

      <div>
        <Label
          htmlFor={commentId}
          hint={`${formatNumber(comment.length)} / ${formatNumber(MAX_COMMENT_LENGTH)}`}
        >
          Commentaire de l'application
        </Label>
        <textarea
          id={commentId}
          value={comment}
          rows={2}
          maxLength={MAX_COMMENT_LENGTH}
          onChange={(event) => setComment(event.target.value)}
          className={clsx(
            'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm',
            'text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
          )}
        />
      </div>

      <ConfirmationCheckbox
        checked={confirmed}
        onChange={setConfirmed}
        text={settings.confirmationText}
      />

      {failure ? <ErrorBanner message={failure.message} issues={failure.issues} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" disabled={!canSubmit} loading={submitting}>
          <Rocket className="h-4 w-4" aria-hidden="true" />
          Mettre à jour
        </Button>
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel} type="button">
            Annuler
          </Button>
        ) : null}
        {missing.length > 0 ? (
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Il manque : {missing.join(', ')}.
          </span>
        ) : null}
      </div>
    </form>
  );
}
